import { inject, injectable } from 'inversify';
import ConnectionStatManager from './ConnectionStatsManager';
import Media from '../media';
import { IPeersService } from '../Peers';
import { ResultQValues, TransportsStatsProviderParams, TransportsWebRTCStats } from './types';
import { TOKEN } from '../../inversify.tokens';
import type { LoggerFactory } from '../../types/container';
import Logger from '../Logger';
import { serializeError } from 'serialize-error';
import type { ScoresCalculator } from '../../qoe/ScoresCalculator';
import type { MOSScores } from '../../qoe/types';
import { INBOUND_Q_PERCENTILE } from '../../qoe/constants';
import { getLocalStorageItem } from '../analyticsApiClient/helper';
import { linearPercentile } from '../../qoe/helpers';

@injectable()
export default class TransportsStatsProvider {
  #initialized = false;

  readonly #logger: Logger;

  #sendTransportConnectionManager?: ConnectionStatManager;

  #receiveTransportConnectionManager?: ConnectionStatManager;

  #media?: Media;

  #peers?: IPeersService;

  readonly #scoresCalculator: ScoresCalculator;

  readonly #inboundQPercentile = INBOUND_Q_PERCENTILE;

  constructor(
    @inject(TOKEN.LoggerFactory) loggerFactory: LoggerFactory,
    @inject(TOKEN.ScoresCalculator) scoresCalculator: ScoresCalculator,
  ) {
    this.#logger = loggerFactory('IntegrationsService');
    this.#scoresCalculator = scoresCalculator;
    this.#inboundQPercentile = getLocalStorageItem('qosQPercentile') ?? this.#inboundQPercentile;
  }

  init({
    sendTransportConnectionManager,
    receiveTransportConnectionManager,
    media,
    peers,
  }: TransportsStatsProviderParams): void {
    this.#sendTransportConnectionManager = sendTransportConnectionManager;
    this.#receiveTransportConnectionManager = receiveTransportConnectionManager;
    this.#media = media;
    this.#peers = peers;
    this.#initialized = true;
  }

  getStats(): TransportsWebRTCStats {
    try {
      return this.doGetStats();
    } catch (error: unknown) {
      this.#logger.error('Failed to get stats', {
        error: serializeError(error),
      });

      return {};
    }
  }

  clear(): void {
    this.#sendTransportConnectionManager = undefined;
    this.#receiveTransportConnectionManager = undefined;
    this.#media = undefined;
    this.#peers = undefined;
  }

  private doGetStats(): TransportsWebRTCStats {
    if (!this.#initialized) {
      throw new Error('TransportsStatsProvider is not initialized');
    }

    const stats: TransportsWebRTCStats = {};

    if (this.#sendTransportConnectionManager?.connectionStats?.id) {
      const qValues = this.calculateOutboundTracksResultQValues();
      stats.outbound = {
        ...this.#sendTransportConnectionManager.connectionStats,
        bitrate: this.calculateOutboundTracksBitrate(),
        Qav: qValues.Qav,
        Qa: qValues.Qa,
        Qv: qValues.Qv,
      };
    }

    if (this.#receiveTransportConnectionManager?.connectionStats?.id) {
      const qValues = this.calculateInboundTracksResultQValues();
      stats.inbound = {
        ...this.#receiveTransportConnectionManager.connectionStats,
        bitrate: this.calculateInboundTracksBitrate(),
        Qav: qValues.Qav,
        Qa: qValues.Qa,
        Qv: qValues.Qv,
      };
    }

    return stats;
  }

  private calculateInboundTracksBitrate(): number {
    if (this.#peers === undefined) {
      throw new Error('Peers is undefined');
    }

    return this.#peers.hosts().reduce((sumPeersBitrate, peer) => {
      if (peer.isMe) {
        return sumPeersBitrate;
      }

      const peerBitrate = Array.from(peer.tracks.values()).reduce((sumTracksBitrate, track) => {
        const trackBitrate = track.kind === 'video'
          ? track.videoWebRTCStats?.bitrate
          : track.audioWebRTCStats?.bitrate;

        return sumTracksBitrate + (trackBitrate ?? 0);
      }, 0);

      return sumPeersBitrate + peerBitrate;
    }, 0);
  }

  private calculateInboundTracksResultQValues(): ResultQValues {
    if (this.#peers === undefined) {
      throw new Error('Peers prop is not defined');
    }

    const peersQavValues: number[] = [];
    const peersQaValues: number[] = [];
    const peersQvValues: number[] = [];

    for (const peer of this.#peers.hosts()) {
      if (peer.isMe) {
        continue;
      }

      const { Qa: worstQa, Qv: worstQv } = this.getWorstQValues(Array.from(peer.tracks.values()));

      if (worstQa !== undefined) {
        peersQaValues.push(worstQa);
      }

      if (worstQv !== undefined) {
        peersQvValues.push(worstQv);
      }

      const peerQav = worstQa === undefined || worstQv === undefined
        ? worstQa ?? worstQv
        : this.#scoresCalculator.calculateAVCompositeMOS(worstQa, worstQv);

      if (peerQav !== undefined) {
        peersQavValues.push(peerQav);
      }
    }

    const comparator = (a: number, b: number): number => a - b;
    peersQavValues.sort(comparator);
    peersQaValues.sort(comparator);
    peersQvValues.sort(comparator);

    return {
      Qav: peersQavValues.length > 0 ? linearPercentile(peersQavValues, this.#inboundQPercentile) : undefined,
      Qa: peersQaValues.length > 0 ? linearPercentile(peersQaValues, this.#inboundQPercentile) : undefined,
      Qv: peersQvValues.length > 0 ? linearPercentile(peersQvValues, this.#inboundQPercentile) : undefined,
    };
  }

  private calculateOutboundTracksResultQValues(): ResultQValues {
    if (this.#media === undefined) {
      throw new Error('Media prop is not defined');
    }

    const tracks = this.#media.getAllTracks().filter((track) => track.isPublished);
    const { Qa: worstQa, Qv: worstQv } = this.getWorstQValues(tracks);
    let Qav: number | undefined = undefined;

    if (worstQa !== undefined && worstQv !== undefined) {
      Qav = this.#scoresCalculator.calculateAVCompositeMOS(worstQa, worstQv);
    }

    return {
      Qav: Qav ?? worstQa ?? worstQv,
      Qa: worstQa,
      Qv: worstQv,
    };
  }

  private calculateOutboundTracksBitrate(): number {
    if (this.#media === undefined) {
      throw new Error('Media is undefined');
    }

    return this.#media.getAllTracks().reduce((sumTracksBitrate, track) => {
      if (!track.isPublished) {
        return sumTracksBitrate;
      }

      const trackBitrate = track.kind === 'video'
        ? track.videoWebRTCStats?.bitrate
        : track.audioWebRTCStats?.bitrate;

      return sumTracksBitrate + (trackBitrate ?? 0);
    }, 0);
  }

  private getWorstQValues(tracks: { connectionScores: MOSScores }[]): MOSScores {
    let worstQa: number | undefined = undefined;
    let worstQv: number | undefined = undefined;

    for (const track of tracks) {
      const { Qa, Qv } = track.connectionScores;

      if (Qa !== undefined && (worstQa === undefined || Qa < worstQa)) {
        worstQa = Qa;
      }

      if (Qv !== undefined && (worstQv === undefined || Qv < worstQv)) {
        worstQv = Qv;
      }
    }

    return { Qa: worstQa, Qv: worstQv };
  }
}
